import dayjs from 'dayjs';
export { default as dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import duration from 'dayjs/plugin/duration.js';
import isBetween from 'dayjs/plugin/isBetween.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import timezone from 'dayjs/plugin/timezone.js';
import updateLocale from 'dayjs/plugin/updateLocale.js';
import utc from 'dayjs/plugin/utc.js';

// src/legacy.ts
var CurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD"
});
var USCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
  minimumFractionDigits: 2
});
var NumberFormatter = new Intl.NumberFormat("en-US");
var PercentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 2
});
var USDFormatter = (value) => {
  return CurrencyFormatter.format(value).replace(/^(\D+)/, "$1 ");
};
function toSafeNumber(value, numFunc = Number) {
  if (value === null) return null;
  if (typeof value === "undefined") return null;
  if (Number.isNaN(value)) return null;
  const deAlphaNumeric = `${value}`.replace(/[^\d.]*/g, "");
  const converted = numFunc(deAlphaNumeric);
  if (deAlphaNumeric.length === 0) return null;
  if (converted === null) return null;
  if (Number.isNaN(converted)) return null;
  return converted;
}
function toCikValue(cik) {
  const safeCik = toSafeNumber(cik);
  if (!safeCik) return null;
  return `${safeCik}`.padStart(10, "0");
}
function currencyToNumber(currency) {
  return Number(currency.replace(/[^0-9.-]+/g, ""));
}

// src/formatter.ts
var LocalePairs = [["en", "en-US"]];
var LocaleMap = new Map(LocalePairs);
var Formatter = class {
  constructor(locale) {
    this.locale = LocaleMap.get(locale) ?? locale;
  }
  /**
   * @param options Provides options to the Intl.NumberFormat
   * @description Builds a new Intl.NumberFormat instance with the given options.
   */
  build(options) {
    return new Intl.NumberFormat(this.locale, options ?? {});
  }
  /**
   * @param value The value to format. This can be a number, string, or bigint.
   * @param options Number format options
   */
  number(value, options) {
    return this.build(options).format(Number(value));
  }
  /**
   * @param value The value to format into a percentage. This can be a number, string, or bigint.
   * @param options Number format options
   */
  percent(value, options) {
    return this.build({
      ...options,
      style: "percent"
    }).format(Number(value));
  }
  currency(value, options = {}) {
    const { currency = "USD", ...rest } = options;
    return this.build({
      ...rest,
      style: "currency",
      currency
    }).format(Number(value));
  }
};
var formatterCache = /* @__PURE__ */ new Map();
function resolveLocale(locale = "en") {
  return LocaleMap.get(locale) ?? locale;
}
function getFormatter(locale = "en") {
  const resolved = resolveLocale(locale);
  if (!formatterCache.has(resolved)) {
    formatterCache.set(resolved, new Formatter(resolved));
  }
  return formatterCache.get(resolved);
}
function toNumberValue(value) {
  if (value === null || value === void 0) {
    return null;
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? null : value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof value === "bigint") {
    return Number(value);
  }
  if (typeof value === "object") {
    if (typeof value.toNumber === "function") {
      try {
        const parsed = value.toNumber();
        return Number.isNaN(parsed) ? null : parsed;
      } catch (error) {
        console.error(error, "Failed to convert value via toNumber()");
      }
    }
    if (typeof value.toString === "function") {
      const parsed = Number(value.toString());
      return Number.isNaN(parsed) ? null : parsed;
    }
  }
  return null;
}
function formatCurrencyValue(value, options, locale) {
  const numeric = toNumberValue(value);
  if (numeric === null) {
    return "";
  }
  const formatter = getFormatter(locale);
  return formatter.currency(numeric, options);
}
function formatNumberValue(value, options, locale) {
  const numeric = toNumberValue(value);
  if (numeric === null) {
    return "";
  }
  const formatter = getFormatter(locale);
  return formatter.number(numeric, options);
}
function formatPercentValue(value, options, locale) {
  const numeric = toNumberValue(value);
  if (numeric === null) {
    return "";
  }
  const formatter = getFormatter(locale);
  return formatter.percent(numeric, options);
}
function formatDateValue(value, options = { month: "numeric", day: "numeric", year: "numeric" }, locale = "en") {
  if (value === null || value === void 0) {
    return "";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat(resolveLocale(locale), options).format(date);
}
dayjs.locale("en");
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(advancedFormat);
dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "%d seconds",
    ss: "%d seconds",
    m: "a minute",
    mm: "%d minutes",
    h: "an hour",
    hh: "%d hours",
    d: "a day",
    dd: "%d days",
    M: "a month",
    MM: "%d months",
    y: "a year",
    yy: "%d years"
  }
});
var isValidDateString = (value, format = "MM/DD/YYYY") => {
  if (typeof value !== "string" || value.length === 0) return false;
  return dayjs(value, format, true).isValid();
};

export { CurrencyFormatter, Formatter, NumberFormatter, PercentFormatter, USCurrencyFormatter, USDFormatter, currencyToNumber, formatCurrencyValue, formatDateValue, formatNumberValue, formatPercentValue, isValidDateString, toCikValue, toNumberValue, toSafeNumber };
